/**
 * Help Center - 6 support articles for Xero App Partner certification
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronDown, BookOpen, Link2, Search, Settings, Shield, HelpCircle } from 'lucide-react';

const ARTICLES = [
  {
    id: 'getting-started',
    title: 'Getting Started with RoniDoc',
    icon: BookOpen,
    content: `
## Welcome to RoniDoc

RoniDoc is an AI-powered document intelligence platform built for Australian accounting firms. It connects to your Xero account, analyses your client financial data, and provides instant insights through natural language queries.

### Step 1: Create Your Account

1. Visit [ronidoc.data-corner.com.au/signup](https://ronidoc.data-corner.com.au/signup)
2. Choose a plan that suits your practice size
3. Enter your email, password, and firm name
4. Complete the signup process

### Step 2: Connect Your Xero Account

1. Navigate to **Settings > Integrations > Xero**
2. Click **Connect to Xero**
3. Log in to your Xero account when prompted
4. Select the organisation(s) you want to connect
5. Authorise RoniDoc to access your financial data (read-only)

### Step 3: Start Asking Questions

Once connected, go to any client and start chatting:
- "What was the revenue for FY2025?"
- "Show me the P&L for last quarter"
- "Compare expenses year over year"

### Step 4: Explore Features

- **Compliance Checker**: Compare client ratios against ATO benchmarks
- **Tax Planning**: Model what-if scenarios for tax optimisation
- **Reports**: Generate branded PDF/Word/Excel reports
- **Multi-Client Intelligence**: Ask questions across all clients at once

### Need Help?

Contact us at [hello@ronidoc.ai](mailto:hello@ronidoc.ai) and we will get back to you within 24 hours.
    `,
  },
  {
    id: 'connect-xero',
    title: 'Connecting to Xero',
    icon: Link2,
    content: `
## How to Connect Your Xero Account

RoniDoc uses Xero's official OAuth 2.0 integration to securely connect to your accounting data. We only request **read-only access** and cannot modify, create, or delete any data in your Xero account.

### What Data Does RoniDoc Access?

| Data Type | Access Level | Purpose |
|-----------|-------------|---------|
| Profit & Loss Reports | Read-only | Financial analysis and AI insights |
| Balance Sheet | Read-only | Asset/liability analysis |
| Trial Balance | Read-only | Reconciliation checks |
| Contacts | Read-only | Client matching via ABN |
| Organisation Details | Read-only | ABN auto-matching |
| Chart of Accounts | Read-only | Account categorisation |

### Step-by-Step Connection Guide

1. **Log in** to your RoniDoc account
2. Go to **Settings > Integrations** or open a client and click **Connect to Xero**
3. Click the **Connect to Xero** button
4. You will be redirected to Xero's login page
5. Enter your Xero credentials
6. Review the permissions (all read-only)
7. Click **Allow Access**
8. You will be redirected back to RoniDoc
9. Your Xero organisations will appear in the connection list

### Connecting Multiple Organisations

You can connect multiple Xero organisations. Each client in RoniDoc can be mapped to a specific Xero organisation:

1. Go to **Clients** and select a client
2. In the client details panel, find **Xero Organization**
3. Select the correct Xero organisation from the dropdown
4. The client is now linked to that Xero data

### ABN Auto-Matching

If your clients have ABN numbers set in both RoniDoc and Xero, the system will automatically match them during the connection process.

### Troubleshooting Connection Issues

**"Xero not connected" error:**
- Your Xero token may have expired. Go to Settings > Integrations and reconnect.

**Wrong organisation showing:**
- Use the Xero Organization dropdown on the client detail page to select the correct org.

**Cannot see financial data:**
- Ensure the Xero organisation has data for the requested period.
- Check that the organisation is correctly mapped to the client.

### Security

- All connections use OAuth 2.0 (industry standard)
- Tokens are encrypted with AES-256-CBC before storage
- Tokens auto-refresh every 25 minutes
- You can disconnect at any time from Settings > Integrations
    `,
  },
  {
    id: 'ai-search',
    title: 'Using AI Search & Chat',
    icon: Search,
    content: `
## How to Use AI-Powered Search

RoniDoc's AI understands natural language. You do not need to learn special commands or syntax. Just ask your question as you would ask a colleague.

### Where to Ask Questions

1. **Client Chat**: Open any client, type your question in the chat panel. The AI will search that client's Xero data and uploaded documents.

2. **Multi-Client Intelligence**: Go to **Ask All Clients** in the sidebar. Ask questions across all connected clients simultaneously.

3. **Manager PA**: Visit [/pa](https://ronidoc.data-corner.com.au/pa) for a mobile-friendly voice assistant. Type or speak your question.

### Example Queries

**Financial Questions:**
- "What was the net profit for FY2025?"
- "Show me revenue by month for the last 12 months"
- "Compare expenses this year vs last year"
- "What is the gross profit margin?"

**Compliance & Tax:**
- "Does this client comply with ATO benchmarks?"
- "What deductions are available for this industry?"
- "Calculate the Division 7A minimum repayment"
- "What is the super guarantee obligation?"

**Multi-Client:**
- "Which clients had declining revenue?"
- "Show me profit margins for all clients"
- "Who has the highest audit risk?"
- "Compare all supermarket clients"

**Document Search:**
- "What does the tax return show for depreciation?"
- "Find the balance sheet from last year"
- "What ABN is on file for this client?"

### Understanding AI Responses

- **Tables**: Financial data is presented in formatted tables
- **Charts**: The AI generates bar, pie, and line charts when appropriate
- **Citations**: When referencing uploaded documents, the source file is cited
- **ATO References**: Compliance answers include links to ATO source material

### Tips for Better Results

1. **Be specific**: "What was revenue in Q3 2025?" is better than "Show me revenue"
2. **Name the client**: "How is Azad Plastering performing?" gives targeted results
3. **Ask follow-ups**: After a response, ask related questions to drill deeper
4. **Use the data source selector**: Choose Xero, Documents, or Both depending on what you need
    `,
  },
  {
    id: 'manage-connections',
    title: 'Managing Xero Connections',
    icon: Settings,
    content: `
## Managing Your Xero Connections

### Viewing Connected Organisations

1. Go to **Settings > Integrations > Xero**
2. You will see a list of all connected Xero organisations
3. Each organisation shows:
   - Organisation name
   - Connection status (active/expired)
   - Last sync time

### Disconnecting from Xero

To disconnect all Xero organisations:

1. Go to **Settings > Integrations > Xero**
2. Click **Disconnect Xero**
3. Confirm the disconnection

This will:
- Revoke all OAuth tokens
- Remove Xero connections from the database
- Stop all data syncing
- **Not** delete any previously generated reports or chat history

### Disconnecting a Single Client

To unlink a specific client from their Xero organisation:

1. Go to **Clients** and select the client
2. Find the **Xero Organization** section
3. Click **Disconnect**

The client record remains in RoniDoc but will no longer pull live Xero data.

### Reconnecting After Disconnection

If your connection expires or you disconnected:

1. Go to **Settings > Integrations > Xero**
2. Click **Connect to Xero**
3. Log in and authorise again
4. Your previous client mappings will be restored automatically

### Sync Status

RoniDoc fetches Xero data on demand (when you ask a question or run a report). There is no background sync that continuously polls Xero. This means:

- Data is always fresh when you query it
- No unnecessary API calls
- Minimal impact on your Xero API limits

### Connection Limits

Connection limits depend on your subscription plan:

| Plan | Xero Connections |
|------|-----------------|
| Trial | 1 |
| Starter | 10 |
| Professional | 50 |
| Enterprise | 200 |
| Scale | 500 |
    `,
  },
  {
    id: 'security',
    title: 'Security & Privacy',
    icon: Shield,
    content: `
## Security & Privacy at RoniDoc

We take the security of your financial data seriously. Here is how we protect your information.

### Data Encryption

**At Rest:**
- All data stored with AES-256 encryption in AWS RDS PostgreSQL
- OAuth tokens encrypted with AES-256-CBC with per-record initialisation vectors
- API keys encrypted before storage

**In Transit:**
- All connections use HTTPS (TLS 1.2+)
- No unencrypted data transmission

### Read-Only Xero Access

RoniDoc requests only read-only access to your Xero data. We **cannot**:
- Create, modify, or delete transactions
- Change contact details
- Alter chart of accounts
- Modify any settings

We **can** only:
- Read financial reports (P&L, Balance Sheet, Trial Balance)
- Read contact information (for client matching)
- Read organisation details (for ABN matching)

### Tenant Isolation

Every accounting firm using RoniDoc operates in a completely isolated environment:
- Row-Level Security (RLS) on all database tables
- No firm can access another firm's data
- Each API request is scoped to the authenticated tenant

### Access Control

- Role-based access: Super Admin, Tenant Admin, Manager, User
- Each role has specific permissions
- Users can only see clients assigned to their firm

### Data Retention

- Your data is retained as long as your account is active
- When you cancel, data is retained for 30 days then permanently deleted
- You can request immediate deletion by contacting support
- Disconnecting from Xero revokes tokens immediately

### Infrastructure

- Hosted on Amazon Web Services (AWS)
- Database: AWS RDS PostgreSQL with automated daily backups
- Compute: AWS Lambda (serverless)
- Storage: AWS S3 with server-side encryption
- CDN: AWS CloudFront

### Compliance

- Australian Privacy Act 1988 compliant
- GDPR-ready for future international expansion
- No data shared with third parties
- AI queries are not used to train models

### Reporting Security Concerns

If you discover a security issue, please contact us immediately at [hello@ronidoc.ai](mailto:hello@ronidoc.ai). We take all reports seriously and will respond within 24 hours.
    `,
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: HelpCircle,
    content: `
## Troubleshooting Common Issues

### Connection Issues

**"Xero not connected" error when viewing client data**
- Your Xero OAuth token may have expired
- Go to Settings > Integrations > Xero and click Reconnect
- If the issue persists, disconnect completely and reconnect

**"Connection limit reached" in Xero**
- This means your Xero API tier limit has been reached
- Contact support to discuss upgrading your Xero connection tier

**Wrong Xero organisation linked to client**
- Go to the client detail page
- Find the Xero Organization dropdown
- Select the correct organisation from the list

### Search & Chat Issues

**AI chat returns "No data found"**
- Ensure the client is connected to the correct Xero organisation
- Check that Xero has data for the requested period
- Try specifying a different date range

**Chat is slow or times out**
- Complex queries across many clients may take 15-30 seconds
- If it times out, try a simpler question first
- Check your internet connection

**Charts not displaying**
- Ensure you are using a modern browser (Chrome, Edge, Safari, Firefox)
- Clear your browser cache and reload

### Document Issues

**Uploaded document not searchable**
- Documents need to be processed after upload (text extraction + indexing)
- Check the document status in the file browser
- PDF files with scanned images may not extract text properly

**Wrong document category detected**
- The auto-categorisation uses AI to detect document type
- You can manually change the category when uploading

### Account Issues

**Cannot log in**
- Check your email and password are correct
- Use the "Forgot Password" link to reset
- Clear browser cookies and try again

**Features locked / upgrade required**
- Some features require higher subscription plans
- Visit the Upgrade page to see available plans
- Contact support for custom enterprise pricing

### Billing Issues

**Payment not showing in history**
- Payment history is fetched from Stripe
- It may take a few minutes to appear after payment
- Check your email for a Stripe receipt

**Need to cancel subscription**
- Go to Settings > Billing
- Click "Cancel subscription"
- Your data is retained for 30 days after cancellation

### Still Need Help?

Contact us at [hello@ronidoc.ai](mailto:hello@ronidoc.ai)

We respond within 24 hours during Australian business hours (AEST/AEDT).
    `,
  },
  {
    id: 'reports',
    title: 'Generating Reports',
    icon: BookOpen,
    content: `
## Generating Client Reports

RoniDoc can generate branded reports in multiple formats from your Xero data and AI analysis.

### Available Report Types

- **Monthly Financial Summary**: Revenue, expenses, profit trends
- **Client Health Report**: Health score breakdown with recommendations
- **Compliance Report**: ATO benchmark comparison with risk flags
- **Tax Planning Report**: What-if scenarios and tax saving opportunities
- **Opportunity Report**: Identified deductions and optimisation strategies

### Export Formats

| Format | Best For |
|--------|----------|
| PDF | Client-facing reports with charts and branding |
| Word (.docx) | Reports that need editing before sending |
| Excel (.xlsx) | Financial data for spreadsheet analysis |
| CSV | Raw data for importing into other systems |

### How to Generate a Report

1. Open a client and start a chat
2. Ask a question like "Generate a compliance report"
3. The AI will produce the analysis
4. Click the **Export** button in the chat
5. Select your preferred format (PDF, Word, Excel, CSV)
6. The report downloads with your firm branding

### Batch Reports

Enterprise plan users can generate the same report across all clients simultaneously:
1. Go to **Reports** in the sidebar
2. Select the report type
3. Choose which clients to include
4. Click **Generate All**

### Report Branding

Reports include your firm's name and are formatted professionally. Contact support to customise the logo and styling.
    `,
  },
  {
    id: 'compliance',
    title: 'ATO Compliance Features',
    icon: Shield,
    content: `
## ATO Compliance Monitoring

RoniDoc includes 95 ATO industry benchmarks and automated compliance checking tools built specifically for Australian accounting practices.

### ATO Compliance Checker

Compares your client's financial ratios against ATO Small Business Benchmarks:

1. Go to **Compliance** in the sidebar
2. Select a Xero-connected client
3. The system automatically:
   - Pulls the latest P&L from Xero
   - Calculates key ratios (cost of sales, labour, rent, motor vehicle)
   - Compares against the client's industry benchmarks
   - Flags ratios outside the expected range

### Audit Risk Predictor

Scores each client's audit risk from 0 to 100:
- **0-30**: Low risk (green)
- **31-60**: Medium risk (amber)
- **61-100**: High risk (red)

Factors include: benchmark compliance, industry type, revenue size, expense ratios, and historical patterns.

### BAS Validator

Estimates GST obligations from Xero P&L data:
- Calculates expected GST collected and paid
- Flags discrepancies between estimated and actual BAS
- Helps catch errors before lodgement

### Division 7A Checker

Scans Balance Sheets for potential Div 7A issues:
- Detects loans from companies to shareholders/associates
- Calculates minimum yearly repayment using ATO benchmark rate
- Estimates tax exposure if loan terms are not met

### Superannuation Compliance

Calculates SG obligations from wages data:
- Current SG rate applied automatically (12% for FY2026)
- Quarterly breakdown with deadline tracking
- Flags underpayments with estimated SG Charge penalty

### FBT Detector

Scans expenses for potential fringe benefits:
- Motor vehicles, entertainment, housing, devices, gifts, parking
- Calculates gross-up and estimated FBT liability
- Highlights applicable exemptions

### Year-End Tax Planning

Generates personalised action items for each client:
- 13 strategies ranked by deadline and dollar impact
- Includes prepaid expenses, super top-ups, asset write-offs
- Exportable as Word document for client meetings
    `,
  },
  {
    id: 'manager-pa',
    title: 'Manager Personal Assistant',
    icon: Search,
    content: `
## Manager Personal Assistant (PA)

The Manager PA is a mobile-first app for firm partners and managers. Access it at [/pa](https://ronidoc.data-corner.com.au/pa).

### Features

**Morning Briefing**
Open the PA and instantly see:
- Priority alerts (overdue deadlines, compliance changes)
- Quick stats (clients, documents, Xero connections)
- Tax opportunities found
- Recent activity across all clients

**Voice & Text Chat**
Use the input bar at the bottom of every page:
- Type a question and press Enter
- Or hold the mic button to speak
- AI searches across all clients, documents, and ATO knowledge

**Firm Pulse**
Traffic light view of all clients:
- Green: Healthy (score 70+)
- Amber: Needs attention (score 40-69)
- Red: Critical (score below 40)
- Tap any client for their full detail card

**Client Quick Card**
Tap any client name to see:
- Health score with factor breakdown
- Financial snapshot (revenue, expenses, profit margin)
- Recent conversations and documents
- Contact details (ABN, email, phone)

**Deadlines Calendar**
All ATO deadlines across all clients in one mobile-friendly list, grouped by date and colour-coded by urgency.

**Weekly Digest**
Summary of the week's activity: new documents, conversations, most active clients, and actions needed.

### Install as App

The PA can be installed on your phone:
- **Android**: Tap "Install" when prompted, or use browser menu > "Add to Home Screen"
- **iPhone**: Tap the Share icon > "Add to Home Screen"

### Language Support

The PA can respond in multiple languages:
- English, Dari/Farsi, Arabic, Chinese, Hindi
- Change language in the settings menu (hamburger icon)
    `,
  },
  {
    id: 'plans-billing',
    title: 'Plans & Billing',
    icon: Settings,
    content: `
## Subscription Plans

### Available Plans

| Plan | Clients | AI Queries/mo | Users | Key Features |
|------|---------|--------------|-------|-------------|
| Free Trial | 3 | 50 | 1 | Basic chat, PDF export |
| Starter | 10 | 500 | 2 | Xero connection, all exports |
| Professional | 50 | 2,000 | 5 | Compliance suite, dashboard, reports |
| Enterprise | 200 | 10,000 | 20 | Tax planning, multi-client, predictive |
| Scale | 500 | 50,000 | Unlimited | All features, API access |

### Free Trial

- 14 days, no credit card required
- 3 clients, 50 AI queries
- Full access to basic features
- Upgrade anytime during or after trial

### Upgrading Your Plan

1. Go to **Settings > Billing**
2. Click **Upgrade Plan**
3. Select your new plan
4. Complete payment via Stripe
5. New features activate immediately

### Viewing Usage

Your current usage is shown on the Billing page:
- AI queries used this period
- Number of clients
- Documents uploaded
- Storage used
- Xero connections

### Payment History

All payments are processed through Stripe. View receipts and invoices on the Billing page.

### Cancelling

1. Go to **Settings > Billing**
2. Click "Cancel subscription"
3. Confirm cancellation
4. Your account downgrades to Free Trial
5. Data is retained for 30 days

### Refunds

Contact [hello@ronidoc.ai](mailto:hello@ronidoc.ai) within 14 days of payment for a full refund.
    `,
  },
];

export default function HelpPage() {
  const [openArticle, setOpenArticle] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#262635] text-white">
      {/* Header */}
      <header className="border-b border-zinc-900">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#7c75f0]" />
            <span className="text-base tracking-[0.15em] font-light">RONIDOC<span className="font-semibold">AI</span></span>
          </Link>
          <Link to="/" className="text-xs text-zinc-500 hover:text-white transition">Back to Home</Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-light mb-2">Help Center</h1>
        <p className="text-zinc-500 mb-10">Everything you need to know about using RoniDoc</p>

        {/* Articles */}
        <div className="space-y-3">
          {ARTICLES.map((article) => {
            const isOpen = openArticle === article.id;
            const Icon = article.icon;

            return (
              <div key={article.id} className="border border-zinc-800/60 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenArticle(isOpen ? null : article.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 hover:bg-zinc-900/30 transition text-left"
                >
                  <Icon className="h-5 w-5 text-[#7c75f0] shrink-0" />
                  <span className="text-base font-medium flex-1">{article.title}</span>
                  <ChevronDown className={`h-4 w-4 text-zinc-600 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 border-t border-zinc-800/40">
                    <div
                      className="prose prose-sm prose-invert max-w-none mt-4
                        prose-headings:text-white prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3
                        prose-h3:text-base prose-h3:mt-4 prose-h3:mb-2
                        prose-p:text-zinc-400 prose-p:leading-relaxed
                        prose-li:text-zinc-400
                        prose-strong:text-white
                        prose-a:text-[#7c75f0] prose-a:no-underline hover:prose-a:underline
                        prose-table:text-sm
                        prose-th:text-left prose-th:text-zinc-300 prose-th:border-zinc-700 prose-th:py-2 prose-th:px-3
                        prose-td:text-zinc-400 prose-td:border-zinc-800 prose-td:py-2 prose-td:px-3
                        prose-code:text-[#7c75f0] prose-code:bg-[#5048e5]/10 prose-code:px-1 prose-code:rounded
                        prose-ol:list-decimal prose-ul:list-disc
                      "
                      dangerouslySetInnerHTML={{
                        __html: article.content
                          .replace(/^## (.*$)/gm, '<h2>$1</h2>')
                          .replace(/^### (.*$)/gm, '<h3>$1</h3>')
                          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
                          .replace(/^\| (.+) \|$/gm, (match) => {
                            const cells = match.split('|').filter(c => c.trim());
                            return `<tr>${cells.map(c => `<td>${c.trim()}</td>`).join('')}</tr>`;
                          })
                          .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
                          .replace(/^- (.+)$/gm, '<li>$1</li>')
                          .replace(/\n\n/g, '</p><p>')
                          .replace(/\n/g, '<br/>')
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Contact */}
        <div className="mt-12 text-center border-t border-zinc-900 pt-8">
          <p className="text-sm text-zinc-500">
            Cannot find what you are looking for? Contact us at{' '}
            <a href="mailto:hello@ronidoc.ai" className="text-[#7c75f0] hover:underline">hello@ronidoc.ai</a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#7c75f0]" />
            <span className="text-xs tracking-[0.15em] font-light text-zinc-600">RONIDOC<span className="font-semibold">AI</span></span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Terms</Link>
            <Link to="/privacy-policy" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Privacy</Link>
            <Link to="/help" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Help</Link>
          </div>
          <p className="text-[11px] text-zinc-800">&copy; {new Date().getFullYear()} RoniDoc AI</p>
        </div>
      </footer>
    </div>
  );
}
